import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider as ReduxProvider } from 'react-redux';
import { ApolloProvider } from '@apollo/client';
import { MuiThemeProvider as MuiNextThemeProvider } from '@material-ui/core/styles';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { LicenseInfo } from '@mui/x-license-pro';
import { StytchProvider } from '@stytch/react';
import { StytchUIClient } from '@stytch/vanilla-js';
import { cloverThemeVars } from 'fe-design-base/styles/clover/vars';
import designBaseTheme from 'fe-design-base/styles/theme';
import MuiThemeProvider from 'material-ui/styles/MuiThemeProvider';
import muiTheme from 'theme/mui';
import muiNextTheme from 'theme/muiNext';

import FlashNotification from 'components/FlashNotification';
import Loader from 'components/Loader';

import getIsEmbedded from 'util/embedded';

import { createApolloClient } from './apolloClient';
import App from './App';
import CommandBar from './CommandBar';
import { MUI_PRO_X_KEY } from './constants';
import Nav from './Nav';

const RootModal = React.lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "MainComp-RootModal" */ '../features/modals/RootModal'
  )
);
const NoAccessManagerLogView = React.lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "MainComp-NoAccessManagerLogView" */ '../features/productGrowth/NoAccessManagerLogView'
  )
);

const EnforcementTooltipWrapperBackbone = React.lazy(() =>
  import('./EnforcementTooltipWrapperBackbone')
);

const ComponentCallbacks = React.lazy(() => import('./ComponentCallbacks'));

const DemoAccountWrapper = React.lazy(() =>
  import(
    /* webpackPreload: true, webpackChunkName: "payroll__demo_account" */ '../features/payroll/DemoAccountView'
  )
);

const LazyLeftNavRouter = React.lazy(() =>
  import(
    /* webpackPreload: 0, webpackChunkName: "LeftNav" */ '../features/nav/LeftNav/LeftNavRouter'
  )
);

export default class RootMounter {
  constructor(store, stytchPublicToken) {
    const stytchOptions = {
      endpointOptions: {
        dfppaDomain: 'telemetry.joinhomebase.com',
      },
    };

    LicenseInfo.setLicenseKey(MUI_PRO_X_KEY);

    this.store = store;
    this.stytch = new StytchUIClient(stytchPublicToken, stytchOptions);
    this.apolloClient = createApolloClient();
  }

  mountAll = () => {
    this.renderRootIfPresent(App, 'react-app-root');
    if (module.hot) {
      module.hot.accept('./App', () =>
        this.renderRootIfPresent(App, 'react-app-root')
      );
    }
    this.renderRootIfPresent(FlashNotification, 'react-flash-notice-root');
    this.renderRootIfPresent(LazyLeftNavRouter, 'react-left-nav-root');
    this.renderRootIfPresent(Nav, 'react-nav-root');
    this.renderRootIfPresent(RootModal, 'react-modal-root');
    this.renderRootIfPresent(
      ComponentCallbacks,
      'react-component-callback-root'
    );
    this.renderRootIfPresent(CommandBar, 'react-command-bar-root');

    // Mount demo account if we're on the demo route
    if (
      window.location.pathname === '/payroll/demo/new' &&
      // demo_controller works for sandbox (staging) only
      window.Homebase.env === 'staging'
    ) {
      this.renderRootIfPresent(DemoAccountWrapper, 'react-app-root');
    }
  };

  renderRootIfPresent = (Component, rootId, props = {}) => {
    if (document.getElementById(rootId)) {
      this.renderRoot(Component, rootId, props);
    }
  };

  mountApp = () => {
    this.renderRootIfPresent(App, 'react-app-root');
  };

  mountHasNoManagerLogAccessView = () => {
    this.renderRootIfPresent(
      NoAccessManagerLogView,
      'react-no-manager-log-access-root'
    );
  };

  mountEnforcementTooltipWrapper = (elementId, props) => {
    this.renderRootIfPresent(
      EnforcementTooltipWrapperBackbone,
      elementId,
      props
    );
  };

  applyEmbeddedStyles = () => {
    const cssRoot = document.querySelector(':root');
    for (const [cssVarKey, cssVarValue] of Object.entries(cloverThemeVars)) {
      cssRoot?.style.setProperty(cssVarKey, cssVarValue);
    }

    const bodyContainer = document.querySelector('.body-container');
    if (bodyContainer) {
      bodyContainer.style.backgroundColor = cloverThemeVars['--mono0'];
    }
  };

  renderRoot = (Component, rootId, props) => {
    const container = document.getElementById(rootId);
    const root = createRoot(container);
    const isEmbedded = getIsEmbedded();

    if (isEmbedded) {
      this.applyEmbeddedStyles();
    }

    root.render(
      <React.Suspense fallback={<Loader isLoading centered />}>
        <StytchProvider stytch={this.stytch}>
          <ApolloProvider client={this.apolloClient}>
            <ReduxProvider store={this.store}>
              <MuiNextThemeProvider theme={muiNextTheme}>
                <MuiThemeProvider muiTheme={muiTheme}>
                  <ThemeProvider theme={designBaseTheme}>
                    <StyledEngineProvider>
                      <Component store={this.store} {...props} />
                    </StyledEngineProvider>
                  </ThemeProvider>
                </MuiThemeProvider>
              </MuiNextThemeProvider>
            </ReduxProvider>
          </ApolloProvider>
        </StytchProvider>
      </React.Suspense>
    );
  };
}
